import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'

import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { DialogModule } from 'primeng/dialog'
import { ProgressSpinnerModule } from 'primeng/progressspinner'
import { catchError, forkJoin, of } from 'rxjs'

import { ButtonComponent } from 'app/_shared/components/button/button.component'
import { AbandonedCartData, AbandonedCartItem } from 'app/_shared/interfaces/AbandonedCart'
import { Product } from 'app/_shared/interfaces/Product'
import { CheckoutService } from 'app/_shared/services/checkout.service'
import { ProductService } from 'app/_shared/services/product.service'
import { PromoCodeCacheService } from 'app/_shared/services/promo-code-cache.service'
import { ToastService } from 'app/_shared/services/toast.service'
import { AppConfigService } from 'app/app-config.service'
import { CartStore } from 'app/store/cart.store'

type RecoveryPreparationResult =
  { status: 'recoverable'; cartItem: any } | { status: 'unavailable' }

@Component({
  selector: 'app-abandoned-cart-recovery',
  imports: [ProgressSpinnerModule, DialogModule, ButtonComponent, TranslatePipe],
  templateUrl: './abandoned-cart-recovery.component.html',
  styleUrl: './abandoned-cart-recovery.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AbandonedCartRecoveryComponent implements OnInit {
  private route = inject(ActivatedRoute)
  private router = inject(Router)
  private checkoutService = inject(CheckoutService)
  private productService = inject(ProductService)
  private toastService = inject(ToastService)
  private promoCodeCache = inject(PromoCodeCacheService)
  private appConfig = inject(AppConfigService)
  private cartStore = inject(CartStore)
  private translate = inject(TranslateService)

  isLoading = signal(true)
  displayReplaceDialog = signal(false)

  private pendingApplyRecovery: (() => void) | null = null

  ngOnInit(): void {
    const cartId = this.route.snapshot.paramMap.get('id')
    if (!cartId) {
      this.navigateWithError(this.translate.instant('ABANDONED_CART.invalidLink'))
      return
    }

    const storefrontId = this.appConfig.getAppConfig.id
    this.checkoutService.getAbandonedCart(storefrontId, cartId).subscribe({
      next: response => this.recoverCart(response.data, storefrontId),
      error: () =>
        this.navigateWithError(this.translate.instant('ABANDONED_CART.cartNoLongerAvailable'))
    })
  }

  private recoverCart(cart: AbandonedCartData, storefrontId: number): void {
    const cartData = cart.cart_data ?? { items: [], gift_card_codes: [] }
    const items: AbandonedCartItem[] = cartData.items ?? []
    if (!items.length) {
      this.navigateWithError(this.translate.instant('ABANDONED_CART.emptyCart'))
      return
    }

    const currentCartHasItems = this.cartStore.items().length > 0
    const slugs = [...new Set(items.map(item => item.slug).filter(Boolean))]

    if (!slugs.length) {
      this.handleRecoveryFailure(
        this.translate.instant('ABANDONED_CART.couldNotRecoverProducts'),
        currentCartHasItems
      )
      return
    }

    const productRequests = slugs.map(slug =>
      this.productService.getProduct(storefrontId, slug).pipe(catchError(() => of(null)))
    )

    forkJoin(productRequests).subscribe({
      next: responses => {
        const productsBySlug = new Map<string, Product>()
        responses.forEach(res => {
          if (!res) return
          const product = res.data
          if (product?.slug) {
            productsBySlug.set(product.slug, product)
          }
        })

        if (!productsBySlug.size) {
          this.handleRecoveryFailure(
            this.translate.instant('ABANDONED_CART.couldNotLoadProducts'),
            currentCartHasItems
          )
          return
        }

        const recoveredItems = items
          .map(item => this.prepareRecoveredCartItem(item, productsBySlug.get(item.slug)))
          .filter(
            (
              result
            ): result is {
              status: 'recoverable'
              cartItem: any
            } => result.status === 'recoverable'
          )

        if (!recoveredItems.length) {
          this.handleRecoveryFailure(
            this.translate.instant('ABANDONED_CART.noRecoverableItems'),
            currentCartHasItems
          )
          return
        }

        const skippedItemsCount = items.length - recoveredItems.length
        const applyRecovery = () => {
          this.cartStore.clearCart()

          recoveredItems.forEach(({ cartItem }) => {
            this.cartStore.addCartItem(cartItem)
          })

          const recoveredCodes = Array.isArray(cartData.gift_card_codes)
            ? cartData.gift_card_codes
            : []
          if (recoveredCodes.length) {
            this.promoCodeCache.save(recoveredCodes)
          } else {
            this.promoCodeCache.clear()
          }

          // Temporarily disabled: do not apply gifting_token during abandoned cart recovery.
          // if (cartData.gifting_token) {
          //   this.cartStore.setGiftToken(
          //     cartData.gifting_token,
          //     cartData.gifting_user_token ?? '',
          //     !!(cartData.hide_prices ?? cart.hide_prices)
          //   )
          // }

          this.isLoading.set(false)

          if (skippedItemsCount > 0) {
            this.toastService.warning(
              this.translate.instant('ABANDONED_CART.partialRecoveryWarning')
            )
          }

          this.router.navigate(['/cart'])
        }

        if (currentCartHasItems) {
          this.isLoading.set(false)
          this.pendingApplyRecovery = applyRecovery
          this.displayReplaceDialog.set(true)
        } else {
          applyRecovery()
        }
      },
      error: () =>
        this.handleRecoveryFailure(
          this.translate.instant('ABANDONED_CART.couldNotLoadProducts'),
          currentCartHasItems
        )
    })
  }

  private prepareRecoveredCartItem(
    item: AbandonedCartItem,
    product: Product | undefined
  ): RecoveryPreparationResult {
    if (item.is_available === false || !product) {
      return { status: 'unavailable' }
    }

    const cartItem = this.buildCartItem(item, product)
    if (!cartItem) {
      return { status: 'unavailable' }
    }

    return {
      status: 'recoverable',
      cartItem
    }
  }

  private buildCartItem(item: AbandonedCartItem, product: Product): any {
    const variant = product.product_variants?.find(v => v.id === item.product_variant_id)
    if (!variant && !item.is_gift_card) return null

    const sizeObj = variant?.product_variant_sizes?.find(
      size => size.id === item.product_size_id || size.size === item.size
    )
    if (!item.is_gift_card && !sizeObj) {
      return null
    }

    let imageUrl: string | undefined
    if (variant?.variant_images?.length) {
      const front = variant.variant_images.find((img: any) => img.type === 'frontImage')
      imageUrl = front ? front.full_path : variant.variant_images[0].full_path
    } else if (item.is_gift_card && product.product_thumbnail) {
      imageUrl = product.product_thumbnail.full_path
    }

    const price = item.is_gift_card
      ? (item.gift_card?.amount_cents ?? product.final_price)
      : sizeObj?.price && sizeObj.price > 0
        ? sizeObj.price
        : product.final_price

    const cartItem: any = {
      name: product.name,
      id: product.id,
      product_id: product.id,
      quantity: item.quantity ?? 1,
      size: item.size ?? sizeObj?.size,
      product_thumbnail: product.product_thumbnail,
      price,
      final_price: price,
      product_size_id: sizeObj?.id ?? item.product_size_id ?? null,
      product_color_id: variant?.id ?? item.product_variant_id ?? item.product_color_id ?? null,
      product_variant_id: variant?.id ?? item.product_variant_id ?? null,
      color: variant?.color,
      image: imageUrl ?? '',
      weight: (sizeObj as any)?.weight ?? 0,
      warehouse: product.warehouse,
      slug: product.slug,
      input_fields: item.custom_input_fields ?? []
    }

    if (item.is_gift_card) {
      cartItem.is_gift_card = item.is_gift_card
      if (item.gift_card) {
        cartItem.email = item.gift_card.recipient_email ?? null
        cartItem.recipient_name = item.gift_card.recipient_name ?? null
        cartItem.note = item.gift_card.note ?? null
        if (item.gift_card.amount_cents != null) {
          cartItem.price = item.gift_card.amount_cents
          cartItem.final_price = item.gift_card.amount_cents
        }
      }
    }

    return cartItem
  }

  acceptReplace(): void {
    this.displayReplaceDialog.set(false)
    this.isLoading.set(true)
    this.pendingApplyRecovery?.()
    this.pendingApplyRecovery = null
  }

  rejectReplace(): void {
    this.displayReplaceDialog.set(false)
    this.pendingApplyRecovery = null
    this.router.navigate(['/cart'])
  }

  private handleRecoveryFailure(message: string, preserveCurrentCart: boolean): void {
    this.isLoading.set(false)
    this.toastService.error(message)
    this.router.navigate([preserveCurrentCart ? '/cart' : '/'])
  }

  private navigateWithError(message: string): void {
    this.handleRecoveryFailure(message, false)
  }
}
